import {
  BadgeColor,
  Button,
  DataTable,
  DataTableSort,
  Grid,
  StatusChip,
  Text,
} from "@pushpress/shared-components";
import { toast } from "react-toastify";
import { useCallback, useMemo, useState } from "react";
import UserAvatar from "../../../../components/common/UserAvatar/UserAvatar";
import { defaultToastOptions, joinStrings } from "../../../../utils/misc";
import EmptyTableView from "./EmptyTableView";
import { getMemberPayment, sortMembers } from "../../helpers";
import { Info } from "lucide-react";
import { DATA_NOT_AVAILABLE } from "../../constants";
import { MemberDataType, StripeBillingDataType } from "../../types";
import { useQueryClient } from "@tanstack/react-query";
import {
  usePostCreateMultipleBillingRecords,
  usePostRejectPaymentMethod,
} from "../../api/migrationAssistantApi.hooks";

export type MemberSortType = "name";

export default function MatchedTabView({
  membersData,
  stripeBillingData,
  setIsMatchingModalVisible,
  setSelectedMemberId,
  setShowMultiPaymentsDrawer,
}: {
  membersData: MemberDataType[];
  stripeBillingData: StripeBillingDataType["data"];
  setIsMatchingModalVisible: (value: boolean) => void;
  setSelectedMemberId: (value: number) => void;
  setShowMultiPaymentsDrawer: (value: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { mutate: rejectPaymentMethod, isLoading: rejectPaymentMethodLoading } =
    usePostRejectPaymentMethod();
  const {
    mutate: acceptPaymentMethods,
    isLoading: acceptPaymentMethodsLoading,
  } = usePostCreateMultipleBillingRecords();

  const hasData = membersData.length > 0;

  const hasMultiplePaymentMethods = useCallback(
    (member: MemberDataType) => {
      return stripeBillingData[member.stripeId].paymentMethods.data.length > 1;
    },
    [stripeBillingData],
  );

  const getBillingDataForMember = useCallback(
    (member: MemberDataType) => {
      if (typeof stripeBillingData === "string" || !member.stripeId) {
        return DATA_NOT_AVAILABLE;
      }

      if (member.matchAccepted === false) {
        return <></>;
      }

      return stripeBillingData[member.stripeId]?.paymentMethods.data.map(
        (paymentMethod) => {
          if (paymentMethod.card) {
            const cardBrand = paymentMethod.card.brand;
            const brandName = `${cardBrand.slice(0, 1).toUpperCase()}${cardBrand
              .slice(1)
              .toLowerCase()} `;
            return (
              <div key={paymentMethod.id}>
                {brandName} •••• {paymentMethod.card.last4}
              </div>
            );
          }

          if (paymentMethod.us_bank_account) {
            const bankName = paymentMethod.us_bank_account.bank_name;
            const brandName = `${bankName.slice(0, 1).toUpperCase()}${bankName
              .slice(1)
              .toLowerCase()} `;
            return (
              <div key={paymentMethod.id}>
                {brandName} •••• {paymentMethod.us_bank_account.last4}
              </div>
            );
          }
          return DATA_NOT_AVAILABLE;
        },
      );
    },
    [stripeBillingData],
  );

  const getPaymentStatus = (member: MemberDataType) => {
    if (member.matchAccepted) {
      return <StatusChip color={BadgeColor.Success} label="PAYMENT ACTIVE" />;
    }

    if (member.matchAccepted === false) {
      return <StatusChip color={BadgeColor.Error} label="REJECTED" />;
    }
    return <StatusChip color={BadgeColor.Warning} label="PENDING" />;
  };

  const handleRejectPaymentMethod = useCallback(
    (userId: number) => {
      rejectPaymentMethod(userId, {
        onSuccess: () => {
          toast.success("Payment method rejected", defaultToastOptions);
          queryClient.invalidateQueries({
            queryKey: ["fetchMigratingMembers"],
          });
        },
        onError: () => {
          toast.error(
            "There was an error rejecting payment method",
            defaultToastOptions,
          );
        },
      });
    },
    [rejectPaymentMethod, queryClient],
  );

  const handleAcceptSinglePaymentMethod = useCallback(
    (member: MemberDataType) => {
      if (typeof stripeBillingData === "string") {
        toast.error(
          "There was an error accepting payment method. No payment method found.",
          defaultToastOptions,
        );
        return;
      }

      const billingReference: string =
        stripeBillingData[member.stripeId].paymentMethods.data[0].id;

      if (!billingReference) {
        toast.error(
          "There was an error accepting payment method. No payment method found.",
          defaultToastOptions,
        );
        return;
      }

      acceptPaymentMethods(
        {
          data: [
            {
              userId: member.id,
              userUuid: member.userUuid,
              paymentReference: billingReference,
              customerReference: member.stripeId,
              isPermanent: true,
              // For a single payment method, set as "primary" by default
            },
          ],
        },
        {
          onSuccess: () => {
            toast.success("Payment method accepted", defaultToastOptions);
            queryClient.invalidateQueries({
              queryKey: ["fetchMigratingMembers"],
            });
          },
          onError: () => {
            toast.error(
              "There was an error accepting payment method",
              defaultToastOptions,
            );
          },
        },
      );
    },
    [acceptPaymentMethods, queryClient, stripeBillingData],
  );

  const columns = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        isSortable: true,
        width: "25%",
        widthMobile: "auto",
        cell: (member: MemberDataType) => (
          <Grid container alignItems="center">
            <Grid item>
              <UserAvatar data={member} size="medium" />
            </Grid>
            <Grid item>
              <Text variant="body-lg" color="wolf-800">
                {joinStrings(member.firstName, member.lastName)}
              </Text>
              <Text variant="body-lg" color="wolf-600">
                {member.email || "—"}
              </Text>
            </Grid>
          </Grid>
        ),
      },
      {
        key: "stripeEmail",
        header: "Stripe Email",
        width: "25%",
        widthMobile: "auto",
        cell: (member: MemberDataType) => {
          if (typeof stripeBillingData === "string" || !member.stripeId) {
            return DATA_NOT_AVAILABLE;
          }
          return (
            stripeBillingData[member.stripeId]?.customerData.email ||
            DATA_NOT_AVAILABLE
          );
        },
      },
      {
        key: "payment",
        header: "Payment",
        width: "20%",
        widthMobile: "auto",
        cell: (member: MemberDataType) =>
          member.billingBrand && member.billingLastFour ? (
            <div className="tw-text-slate-600">{getMemberPayment(member)}</div>
          ) : (
            <Grid container>
              <Grid item>{getBillingDataForMember(member)}</Grid>
            </Grid>
          ),
      },
      {
        key: "status",
        header: "Status",
        hideOnMobile: true,
        width: "15%",
        mobileWidth: "auto",
        cell: (member: MemberDataType) => {
          return getPaymentStatus(member);
        },
      },
      {
        key: "action",
        header: "",
        width: "20%",
        widthMobile: "auto",
        cell: (member: MemberDataType) => {
          if (member.matchAccepted === null) {
            return (
              <div className="tw-flex tw-items-center tw-gap-4">
                <Info
                  onClick={() => {
                    setIsMatchingModalVisible(true);
                    setSelectedMemberId(member.id);
                  }}
                />
                <div className="tw-flex tw-flex-col tw-gap-2 md:tw-flex-row">
                  <Button
                    buttonType="secondary"
                    text="Reject"
                    onClick={() => handleRejectPaymentMethod(member.id)}
                  />
                  <Button
                    text="Accept"
                    onClick={() => {
                      if (hasMultiplePaymentMethods(member)) {
                        setSelectedMemberId(member.id);
                        setShowMultiPaymentsDrawer(true);
                      } else {
                        handleAcceptSinglePaymentMethod(member);
                      }
                    }}
                  />
                </div>
              </div>
            );
          }
          return <></>;
        },
      },
    ],
    [
      stripeBillingData,
      getBillingDataForMember,
      setIsMatchingModalVisible,
      setSelectedMemberId,
      handleRejectPaymentMethod,
      hasMultiplePaymentMethods,
      setShowMultiPaymentsDrawer,
      handleAcceptSinglePaymentMethod,
    ],
  );

  const [sort, setSort] = useState<DataTableSort<MemberSortType>>({
    key: "name",
    order: "asc",
  });

  const handleSort = (value: DataTableSort<MemberSortType>) => {
    setSort(value);
  };

  const data = useMemo(() => {
    return sortMembers(membersData, sort, []);
  }, [membersData, sort]);

  return (
    <Grid item md={12} xs={12} marginY={3}>
      {!hasData && <EmptyTableView />}
      {hasData && (
        <DataTable
          loading={acceptPaymentMethodsLoading || rejectPaymentMethodLoading}
          data={data}
          columns={columns}
          sort={sort}
          getRowId={(row) => String(row.id)}
          onSort={handleSort}
        />
      )}
    </Grid>
  );
}
